import React, { useMemo } from 'react';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
} from 'recharts';

import { Loader } from 'components/Loader';

import { useCellRates } from '../api/getCellRates';
import { formatRate, rateCurve, rateStats } from '../utils/rates';
import { SetupOverlay } from './SetupOverlay';

const Description = ({ title, name, description }) => (
  <Stack gap={1}>
    <Typography variant="h6" fontWeight={700}>
      {`${title}: ${name}`}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={400}
      color={description ? 'text.primary' : 'text.secondary'}
      whiteSpace="pre-line"
    >
      {description || 'No description'}
    </Typography>
  </Stack>
);

const Stat = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" gap={2}>
    <Typography variant="body2" fontWeight={700}>
      {label}
    </Typography>
    <Typography variant="body2">{formatRate(value)}</Typography>
  </Stack>
);

const RatesChart = ({ rates }) => {
  const { palette } = useTheme();
  const { curve, ticks, densityTicks } = useMemo(
    () => rateCurve(rates.map(({ rate }) => rate)),
    [rates]
  );
  const axis = {
    tickLine: false,
    axisLine: { stroke: palette.grey[300] },
    tick: { fontSize: 12, fill: palette.text.secondary },
  };

  return (
    <Box height={280} aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={curve} margin={{ top: 8, right: 16, bottom: 0, left: 16 }}>
          <CartesianGrid stroke={palette.grey[200]} />
          <XAxis
            {...axis}
            type="number"
            dataKey="rate"
            domain={[ticks[0], ticks[ticks.length - 1]]}
            ticks={ticks}
            tickFormatter={formatRate}
          />
          <YAxis hide domain={[0, densityTicks[densityTicks.length - 1]]} ticks={densityTicks} />
          <Area
            type="monotone"
            dataKey="density"
            stroke={palette.primary.main}
            strokeWidth={2}
            fill={alpha(palette.primary.main, 0.12)}
            isAnimationActive={false}
          />
          <Scatter
            data={rates.map(({ rate }) => ({ rate, density: 0 }))}
            dataKey="density"
            fill={palette.primary.dark}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const RateCellOverlay = ({ id, procurementName, lot, role, level, onClose }) => {
  const { data: rates, isFetchedAfterMount } = useCellRates(
    id,
    lot.lot_id,
    role.role_id,
    level.level_id
  );
  const stats = rates.length > 0 && rateStats(rates.map(({ rate }) => rate));
  const title = `${role.name} (${level.name})`;

  return (
    <SetupOverlay
      procurementName={procurementName}
      crumbs={[
        { label: 'Rates', onClick: onClose },
        { label: lot.name, onClick: onClose },
        { label: title },
      ]}
      onClose={onClose}
    >
      <Stack gap={3} width="100%" maxWidth={1280} mx="auto">
        <Stack gap={0.5}>
          <Typography variant="h4" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`${lot.name} · Max rates in SEK/h`}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={4}
          p={4}
          border="1px solid"
          borderColor="border.main"
          borderRadius={2}
          boxShadow={25}
          bgcolor="background.paper"
        >
          <Stack flex={1} gap={3} minWidth={0}>
            <Description title="Role" name={role.name} description={role.description} />
            <Description title="Level" name={level.name} description={level.description} />
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack flex={1} gap={4} minWidth={0}>
            {!isFetchedAfterMount ? (
              <Loader />
            ) : !stats ? (
              <Typography color="text.secondary">No supplier has set a rate yet</Typography>
            ) : (
              <>
                <Stack gap={2}>
                  <RatesChart rates={rates} />
                  <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={6} rowGap={0.5}>
                    <Stat label="Max" value={stats.max} />
                    <Stat label="Average" value={stats.average} />
                    <Stat label="Min" value={stats.min} />
                    <Stat label="Median" value={stats.median} />
                  </Box>
                </Stack>

                <Stack gap={1} component="section" aria-label="Supplier rates">
                  <Typography variant="h6" fontWeight={700}>
                    Supplier rates
                  </Typography>
                  {rates.map(({ fac_id, name, rate }) => (
                    <Stack key={fac_id} direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" fontWeight={600}>
                        {name}
                      </Typography>
                      <Typography variant="body2">{formatRate(rate)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </SetupOverlay>
  );
};
